import React, { useEffect, useMemo, useReducer } from 'react';
import { withRouter, Route, Switch, Redirect } from 'react-router-dom';
import {
  CSSTransition,
  Transition,
  TransitionGroup,
} from 'react-transition-group';
import MediaQuery from 'react-responsive';

import FirmOverview from 'scenes/FirmOverview';
import FirmReviews from 'scenes/FirmReviews';
import FirmAbout from 'scenes/FirmAbout';
import FirmPortfolio from 'scenes/FirmPortfolio';
import FirmStrategy from 'scenes/FirmStrategy';
import ReviewFormSwitcher from 'components/ReviewFormSwitcher';
import PageLayout from 'layouts/PageLayout';
import Sidebar from 'scenes/Sidebar';
import FirmEmployees from 'scenes/Sidebar/components/FirmEmployees';
import CtaBanner from 'scenes/Sidebar/components/CtaBanner';
import RelatedJobs from 'scenes/Sidebar/components/RelatedJobs';
import FirmHeader from 'scenes/FirmHeader';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import BasicDialog from 'scenes/Dialog/components/BasicDialog';
import ConfirmDialog from 'scenes/Dialog/components/ConfirmDialog';
import PagePagination from 'components/PagePagination';
import FirmOverviewContentLoader from 'components/FirmOverviewContentLoader';
import FiltersContentLoader from 'components/FiltersContentLoader';
import FirmReviewsContentLoader from 'components/FirmReviewsContentLoader';
import HorizontalLoader from 'components/HorizontalLoader';
import FullWidthCarousel from 'components/FullWidthCarousel';
import FirmHeaderContentLoader from 'components/FirmHeaderContentLoader';
import FormDialog from 'scenes/Dialog/components/FormDialog';
import FirmEditLogoForm from 'scenes/Form/components/FirmEditLogoForm';

import withNetworkActions from 'hocs/withNetworkActions';
import { ROUTE, ROUTE_REGEXP } from 'constants/route';
import { ENDPOINT } from 'constants/endpoints';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation.js';
import { Permission, Role, showNoCandidateProfileDialog } from 'helpers/user';
import { useApi } from 'hooks/useApi';
import { useReviewAction } from 'hooks/useReviewAction';

import { initialState } from './initialState';
import { ACTION, reducer } from 'pages/Firm/reducer';

import ctaBackground from 'assets/images/cta_back_signup.jpg';
import {
  handleEnterAnimation,
  handleExitAnimation,
} from 'animations/timelines/switch-transitions.js';
import './styles.css';

const Firm = props => {
  const {
    user,
    history,
    match: {
      params: { firmname, tab, formType },
    },
    location: { pathname },
    networkAction,
    globalOptions,
  } = props;

  const [state, dispatch] = useReducer(reducer, initialState);

  const api = useApi();

  const {
    rate: rateReview,
    view: handleClickReviewLink,
    isAllowedRate: handleBeforeReviewRatingUpdate,
  } = useReviewAction(user, globalOptions);

  useEffect(() => {
    dispatch({ type: ACTION.FIRM_DATA_REQUEST });

    api.get(
      ENDPOINT.ENTITY.FIRM,
      { firmname },
      {
        handleNotFound: () => history.replace(ROUTE.ERROR_404),
        handleSuccess: response => {
          dispatch({
            type: ACTION.FIRM_DATA_SUCCESS,
            data: {
              ...response,
              baseUrl: `/firm/${firmname}`,
            },
          });
        },
      }
    );
  }, [firmname]);

  useEffect(() => {
    if (!tab && state.tabs.reviews.data.page) {
      fetchReviewsData({
        sortBy: state.sortBy,
        firmId: state.firm.FirmID,
        page: 0,
      });
    }
  }, [tab]);

  const currentNavTab = useMemo(() => {
    const [result] = tab ? state.nav.filter(t => t.alias === tab) : state.nav;
    return result;
  }, [tab, state.nav]);

  const fetchReviewsData = (params = {}) => {
    api.get(
      ENDPOINT.COLLECTION.REVIEWS,
      { ...params, includeComments: 1 },
      {
        handleSuccess: reviews =>
          dispatch({ type: ACTION.FIRM_REVIEWS_SUCCESS, data: reviews }),
      }
    );
  };

  const handleClickReviewPage = page => {
    fetchReviewsData({
      sortBy: state.reviewsSortBy,
      firmId: state.firm.FirmID,
      page,
    });
  };

  const handleReviewSorted = sortBy => {
    if (sortBy !== state.reviewsSortBy) {
      dispatch({ type: ACTION.FIRM_REVIEWS_SORT_CHANGE, data: sortBy });
      fetchReviewsData({
        sortBy,
        firmId: state.firm.FirmID,
        page: state.tabs.reviews.data.page,
      });
    }
  };

  const handleChangeForm = type =>
    history.push(`${state.baseUrl}/add-${type}-review`);

  const handleReviewRatingUpdate = (review, value) => {
    rateReview(review, value).then(responseData => {
      if (responseData) {
        dispatch({ type: ACTION.FIRM_REVIEW_UPDATE, data: responseData });
      }
    });
  };

  const updateFirmRating = value => {
    api.post(
      ENDPOINT.ACTION.FIRM.POST_RATING,
      {
        id: state.firm.FirmID,
        mark: value,
      },
      {
        handleError: response => ResponseErrorDialog({ response }),
        handleSuccess: responseData => {
          dispatch({
            type: ACTION.FIRM_RATING_UPDATE,
            data: { responseData, value },
          });
          BasicDialog({ bodyRender: 'Thank you for your response' });
        },
      }
    );
  };

  const gotoReviewsTab = () =>
    tab !== 'reviews' && history.push(`${state.baseUrl}/reviews`);

  const gotoFormReview = () => {
    if (!user.isLoggedIn || Role.isCandidate(user, true)) {
      history.push(`${state.baseUrl}/add-company-review`);
    }
  };

  const handleDealButton = state.permissions.AllowedChangeDealMark
    ? // no permission =>  no action =>  no buttons render
      ({ Type, PortfolioID }, mark) => {
        dispatch({
          type: ACTION.FIRM_PORTFOLIO_ITEM_MARK_UPDATE_REQUEST,
          data: { Type, PortfolioID },
        });

        api.post(
          ENDPOINT.ACTION.FIRM.POST_PORTFOLIO_DEAL_MARK,
          {
            id: state.firm.FirmID,
            emId: PortfolioID,
            mark,
          },
          {
            handleError: response => ResponseErrorDialog({ response }),
            handleSuccess: responseData => {
              dispatch({
                type: ACTION.FIRM_PORTFOLIO_ITEM_MARK_UPDATE_SUCCESS,
                data: { Type, PortfolioID, responseData },
              });
            },
          }
        );
      }
    : null;

  const toggleEditMode = () => {
    if (state.permissions.AllowedEditMod) {
      dispatch({
        type: ACTION.FIRM_TOGGLE_EDIT_MODE,
        data: !state.isEditMode,
      });
    }
  };

  const handlePhotoGallerySorting = items => {
    dispatch({
      type: ACTION.FIRM_PHOTO_SORTING,
      data: items,
    });

    api.post(
      ENDPOINT.ACTION.FIRM.PHOTO.REORDER,
      {
        id: state.firm.FirmID,
        ids: JSON.stringify(items.map(item => item.id)),
      },
      { handleError: response => ResponseErrorDialog({ response }) }
    );
  };

  const handlePhotoDelete = id => {
    ConfirmDialog({
      bodyRender: `Are you sure you want to delete this photo?`,
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
      textCancelBtn: 'No',
    }).then(() => {
      dispatch({
        type: ACTION.FIRM_PHOTO_DELETE_REQUEST,
        data: id,
      });

      api.post(
        ENDPOINT.ACTION.FIRM.PHOTO.DELETE,
        {
          id: state.firm.FirmID,
          photoId: id,
        },
        {
          handleError: response => ResponseErrorDialog({ response }),
          handleSuccess: () =>
            dispatch({
              type: ACTION.FIRM_PHOTO_DELETE_SUCCESS,
              data: id,
            }),
        }
      );
    });
  };

  const handlePhotoUpload = photo => {
    dispatch({
      type: ACTION.FIRM_PHOTO_UPLOAD_SUCCESS,
      data: photo,
    });
  };

  const togglePhotoGallerySortingMode = () => {
    dispatch({
      type: ACTION.FIRM_PHOTO_TOGGLE_SORTING_MODE,
      data: !state.isPhotosSortable,
    });
  };

  const toggleTabActive = () => {
    const data = {
      alias: currentNavTab.alias,
      isActive: !currentNavTab.isActive,
    };

    let isEmpty = false;

    if ('about' === currentNavTab.alias) {
      const {
        descriptionBlock,
        descriptionSubBlocks,
        awardsBlock,
        photosBlock,
      } = state.tabs.about.data;
      isEmpty = !(
        descriptionBlock ||
        (descriptionSubBlocks && !!descriptionSubBlocks.length) ||
        awardsBlock ||
        (photosBlock && !!photosBlock.length)
      );
    }

    if ('portfolio' === currentNavTab.alias) {
      const {
        criteria: { Criteria, Sector, Size, Style, Geography },
        firms,
      } = state.tabs.portfolio.data;
      isEmpty = !(
        Criteria ||
        Sector ||
        Size ||
        Style ||
        Geography ||
        (firms && !!firms.length)
      );
    }

    if ('strategy' === currentNavTab.alias) {
      const { funds, investment } = state.tabs.strategy.data;
      isEmpty = !(investment || (funds && !!funds.length));
    }

    if (isEmpty && data.isActive) {
      ConfirmDialog({
        bodyRender:
          'This tab is empty. Are you sure you want to activate this tab?',
        dismissWhenClose: true,
        textSuccessBtn: 'Yes',
        textCancelBtn: 'No',
      }).then(() => {
        setTabActive(data);
      });
    } else {
      setTabActive(data);
    }
  };

  const setTabActive = data => {
    dispatch({
      type: ACTION.FIRM_PHOTO_TOGGLE_TAB_ACTIVE_REQUEST,
      data,
    });

    api.post(
      ENDPOINT.ACTION.FIRM.TOGGLE_TAB,
      {
        id: state.firm.FirmID,
        tab: data.alias,
        isActive: data.isActive ? 'Yes' : 'No',
      },
      {
        handleError: response => {
          ResponseErrorDialog({ response });
          dispatch({
            type: ACTION.FIRM_PHOTO_TOGGLE_TAB_ACTIVE_FAIL,
            data: data,
          });
        },
        handleSuccess: () => {
          dispatch({
            type: ACTION.FIRM_PHOTO_TOGGLE_TAB_ACTIVE_SUCCESS,
            data: data,
          });
        },
      }
    );
  };

  const handleDescriptionBlockSave = data => {
    dispatch({
      type: ACTION.FIRM_ABOUT_DESCRIPTION_BLOCK_SAVE,
      data: data,
    });
  };

  const handleDescriptionBlock = () => {
    if (!state.tabs.about.data.descriptionBlock.disabled) {
      ConfirmDialog({
        bodyRender: `Are you sure you want to delete ${
          state.tabs.about.data.descriptionBlock.Title
        } section?`,
        dismissWhenClose: true,
        textSuccessBtn: 'Yes',
        textCancelBtn: 'No',
      }).then(() => {
        dispatch({
          type: ACTION.FIRM_ABOUT_DESCRIPTION_BLOCK_DELETE_REQUEST,
        });

        api.post(
          ENDPOINT.ACTION.FIRM.ABOUT_BLOCK_DELETE,
          {
            id: state.firm.FirmID,
            blockId: state.tabs.about.data.descriptionBlock.FirmInfoBlockID,
          },
          {
            handleError: response => {
              dispatch({
                type: ACTION.FIRM_ABOUT_DESCRIPTION_BLOCK_DELETE_FAIL,
              });
              ResponseErrorDialog({ response });
            },
            handleSuccess: () => {
              dispatch({
                type: ACTION.FIRM_ABOUT_DESCRIPTION_BLOCK_DELETE_SUCCESS,
              });
            },
          }
        );
      });
    }
  };

  const handleDescriptionSubBlockAdd = data => {
    dispatch({
      type: ACTION.FIRM_ABOUT_DESCRIPTION_SUBBLOCK_ADD,
      data: data,
    });
  };

  const handleDescriptionSubBlockUpdate = data => {
    dispatch({
      type: ACTION.FIRM_ABOUT_DESCRIPTION_SUBBLOCK_UPDATE,
      data: data,
    });
  };

  const handleDescriptionSubBlockDelete = ({ FirmInfoBlockID: id, Title }) => {
    ConfirmDialog({
      bodyRender: `Are you sure you want to delete ${Title} section?`,
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
      textCancelBtn: 'No',
    }).then(() => {
      dispatch({
        type: ACTION.FIRM_ABOUT_DESCRIPTION_SUBBLOCK_DELETE_REQUEST,
        data: id,
      });

      api.post(
        ENDPOINT.ACTION.FIRM.ABOUT_BLOCK_DELETE,
        {
          id: state.firm.FirmID,
          blockId: id,
        },
        {
          handleError: response => {
            dispatch({
              type: ACTION.FIRM_ABOUT_DESCRIPTION_SUBBLOCK_DELETE_FAIL,
              data: id,
            });
            ResponseErrorDialog({ response });
          },
          handleSuccess: () => {
            dispatch({
              type: ACTION.FIRM_ABOUT_DESCRIPTION_SUBBLOCK_DELETE_SUCCESS,
              data: id,
            });
          },
        }
      );
    });
  };

  const handleAwardsBlockSave = data => {
    dispatch({
      type: ACTION.FIRM_ABOUT_AWARDS_BLOCK_SAVE,
      data: data,
    });
  };

  const handleAwardsBlockDelete = () => {
    if (!state.tabs.about.data.awardsBlock.disabled) {
      ConfirmDialog({
        bodyRender: `Are you sure you want to delete ${
          state.tabs.about.data.awardsBlock.Title
        } section?`,
        dismissWhenClose: true,
        textSuccessBtn: 'Yes',
        textCancelBtn: 'No',
      }).then(() => {
        dispatch({
          type: ACTION.FIRM_ABOUT_AWARDS_BLOCK_DELETE_REQUEST,
        });

        api.post(
          ENDPOINT.ACTION.FIRM.ABOUT_BLOCK_DELETE,
          {
            id: state.firm.FirmID,
            blockId: state.tabs.about.data.awardsBlock.FirmInfoBlockID,
          },
          {
            handleError: response => {
              dispatch({
                type: ACTION.FIRM_ABOUT_AWARDS_BLOCK_DELETE_FAIL,
              });
              ResponseErrorDialog({ response });
            },
            handleSuccess: () => {
              dispatch({
                type: ACTION.FIRM_ABOUT_AWARDS_BLOCK_DELETE_SUCCESS,
              });
            },
          }
        );
      });
    }
  };

  const editLogo = () => {
    FormDialog({
      compact: true,
      modeless: false,
      popupTitleStyles: {
        padding: parseInt(state.windowWidth, 10) < 480 ? '0 20px' : '0 25px',
      },
      popupInnerStyles: {
        padding: parseInt(state.windowWidth, 10) < 480 ? '0' : '0 20px',
        marginTop: '45px',
      },
      popupStyles: {
        minHeight: '30vh',
        maxWidth: '452px',
      },
      popupContentStyles: {
        paddingTop: 0,
      },
      params: { id: state.firm.FirmID },
      form: (actions, params) => (
        <FirmEditLogoForm actions={actions} params={params} />
      ),
    }).then(
      response =>
        dispatch({
          type: ACTION.FIRM_LOGO_UPDATE,
          data: response,
        }),
      response => ResponseErrorDialog({ response })
    );
  };

  const deleteLogo = () =>
    ConfirmDialog({
      bodyRender: 'Are you sure you want to delete firm logo?',
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
    }).then(() => {
      api.post(
        ENDPOINT.ACTION.FIRM.DELETE_LOGO,
        { id: state.firm.FirmID },
        {
          handleError: response => ResponseErrorDialog({ response }),
          handleSuccess: () => {
            dispatch({
              type: ACTION.FIRM_LOGO_UPDATE,
              data: { Logo: '' },
            });
          },
        }
      );
    });

  const handleAddFundHistory = data => {
    dispatch({
      type: ACTION.FIRM_STRATEGY_FUND_HISTORY_CREATE,
      data,
    });
  };

  const handleEditFundHistory = data => {
    dispatch({
      type: ACTION.FIRM_STRATEGY_FUND_HISTORY_UPDATE,
      data,
    });
  };

  const handleDeleteFundHistory = ({ FirmFundHistoryID, FundName }) => {
    ConfirmDialog({
      bodyRender: `Are you sure you want to delete ${FundName}?`,
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
      textCancelBtn: 'No',
    }).then(() => {
      dispatch({
        type: ACTION.FIRM_STRATEGY_FUND_HISTORY_DELETE_REQUEST,
        data: FirmFundHistoryID,
      });

      api.post(
        ENDPOINT.ACTION.FIRM.FUND_HISTORY_DELETE,
        {
          id: state.firm.FirmID,
          fundId: FirmFundHistoryID,
        },
        {
          handleError: response => {
            dispatch({
              type: ACTION.FIRM_STRATEGY_FUND_HISTORY_DELETE_FAIL,
              data: FirmFundHistoryID,
            });
            ResponseErrorDialog({ response });
          },
          handleSuccess: () => {
            dispatch({
              type: ACTION.FIRM_STRATEGY_FUND_HISTORY_DELETE_SUCCESS,
              data: FirmFundHistoryID,
            });
          },
        }
      );
    });
  };

  const handleEditInvestmentStrategy = data => {
    dispatch({
      type: ACTION.FIRM_STRATEGY_INVESTMENT_STRATEGY_UPDATE,
      data,
    });
  };

  const handleDeleteInvestmentStrategy = () => {
    if (!state.tabs.strategy.data.investment.disabled) {
      ConfirmDialog({
        bodyRender: `Are you sure you want to delete ${
          state.tabs.strategy.data.investment.Title
        }?`,
        dismissWhenClose: true,
        textSuccessBtn: 'Yes',
        textCancelBtn: 'No',
      }).then(() => {
        dispatch({
          type: ACTION.FIRM_STRATEGY_INVESTMENT_STRATEGY_DELETE_REQUEST,
        });

        api.post(
          ENDPOINT.ACTION.FIRM.INVESTMENT_STRATEGY_DELETE,
          {
            id: state.firm.FirmID,
            strategyId: state.tabs.strategy.data.investment.FirmStrategyID,
          },
          {
            handleError: response => {
              dispatch({
                type: ACTION.FIRM_STRATEGY_INVESTMENT_STRATEGY_DELETE_FAIL,
              });
              ResponseErrorDialog({ response });
            },
            handleSuccess: () => {
              dispatch({
                type: ACTION.FIRM_STRATEGY_INVESTMENT_STRATEGY_DELETE_SUCCESS,
              });
            },
          }
        );
      });
    }
  };

  const handleCriteriaUpdate = data => {
    dispatch({
      type: ACTION.FIRM_PORTFOLIO_CRITERIA_UPDATE,
      data,
    });
  };

  const fetchPortfolioGrid = params => {
    dispatch({
      type: ACTION.FIRM_PORTFOLIO_GRID_REQUEST,
      data: { type: params.type },
    });
    api.get(
      ENDPOINT.COLLECTION.FIRM_PORTFOLIO_ITEMS,
      { ...params, id: state.firm.FirmID },
      {
        handleSuccess: response => {
          if (
            Number(response.page) !== 0 &&
            Number(response.pagesCount) > 0 &&
            !response.rows.length
          ) {
            fetchPortfolioGrid({ ...params, page: Number(response.page) - 1 });
          } else {
            dispatch({
              type: ACTION.FIRM_PORTFOLIO_GRID_SUCCESS,
              data: { type: params.type, grid: response },
            });
          }
        },
      }
    );
  };

  const handleAddPortfolioItem = data => {
    fetchPortfolioGrid({
      type: data.Type,
      page: state.tabs.portfolio.data[data.Type.toLowerCase()].page,
    });
  };

  const handleEditPortfolioItem = data => {
    dispatch({
      type: ACTION.FIRM_PORTFOLIO_ITEM_UPDATE,
      data,
    });
  };

  const handleDeletePortfolioItem = ({ PortfolioID, FirmName, Type }) => {
    ConfirmDialog({
      bodyRender: `Are you sure you want to delete ${FirmName}?`,
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
      textCancelBtn: 'No',
    }).then(() => {
      dispatch({
        type: ACTION.FIRM_PORTFOLIO_ITEM_DELETE_REQUEST,
        data: { PortfolioID, Type },
      });

      api.post(
        ENDPOINT.ACTION.FIRM.FIRM_PROFILE_ITEM_DELETE,
        {
          id: state.firm.FirmID,
          emId: PortfolioID,
        },
        {
          handleError: response => {
            dispatch({
              type: ACTION.FIRM_PORTFOLIO_ITEM_DELETE_FAIL,
              data: { PortfolioID, Type },
            });
            ResponseErrorDialog({ response });
          },
          handleSuccess: () => {
            fetchPortfolioGrid({
              type: Type,
              page: state.tabs.portfolio.data[Type.toLowerCase()].page,
            });
          },
        }
      );
    });
  };

  const handleEditOverview = data => {
    dispatch({
      type: ACTION.FIRM_OVERVIEW_UPDATE,
      data,
    });
  };

  const handleCandidatesAlsoViewedRatingUpdate = (id, mark) => {
    dispatch({
      type: ACTION.CANDIDATES_ALSO_VIEWED_FIRM_RATING_UPDATE_REQUEST,
      data: id,
    });

    api.post(
      ENDPOINT.ACTION.FIRM.POST_RATING,
      {
        id: id,
        mark: mark,
      },
      {
        handleError: response => {
          dispatch({
            type: ACTION.CANDIDATES_ALSO_VIEWED_FIRM_RATING_UPDATE_FAIL,
            data: id,
          });
          ResponseErrorDialog({ response });
        },
        handleSuccess: responseData => {
          const { RatingCount: ResponsesCount, Rating } = responseData;
          dispatch({
            type: ACTION.CANDIDATES_ALSO_VIEWED_FIRM_RATING_UPDATE_SUCCESS,
            data: { FirmID: id, ResponsesCount, Rating },
          });
          BasicDialog({ bodyRender: 'Thank you for your response' });
        },
      }
    );
  };

  const handleCandidatesAlsoViewedBeforeRatingUpdate = () => {
    if (Role.isCandidate(user)) {
      return true;
    } else {
      showNoCandidateProfileDialog(user);
      return false;
    }
  };

  const handlePortfolioItemAddComment = params => {
    const type = params.type.toLowerCase();
    dispatch({
      type: ACTION.FIRM_PORTFOLIO_ADD_COMMENT_REQUEST,
      data: { id: params.portfolioId, type },
    });

    api.post(
      ENDPOINT.ACTION.FIRM.ADD_PORTFOLIO_COMMENT,
      {
        id: state.firm.FirmID,
        emId: params.portfolioId,
        comment: params.comment,
      },
      {
        handleError: response => {
          dispatch({
            type: ACTION.FIRM_PORTFOLIO_ADD_COMMENT_FAIL,
            data: { id: params.portfolioId, type },
          });
          ResponseErrorDialog({ response });
        },
        handleSuccess: responseData => {
          dispatch({
            type: ACTION.FIRM_PORTFOLIO_ADD_COMMENT_SUCCESS,
            data: {
              id: params.portfolioId,
              type: type,
              comment: responseData,
            },
          });
        },
      }
    );
  };

  const handlePortfolioItemRemoveComment = params => {
    const type = params.type.toLowerCase();
    ConfirmDialog({
      bodyRender: `Are you sure you want to delete this comment?`,
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
      textCancelBtn: 'No',
    }).then(() => {
      dispatch({
        type: ACTION.FIRM_PORTFOLIO_REMOVE_COMMENT_REQUEST,
        data: { id: params.portfolioId, type },
      });

      api.post(
        ENDPOINT.ACTION.FIRM.DELETE_PORTFOLIO_COMMENT,
        {
          id: params.commentId,
        },
        {
          handleError: response => {
            dispatch({
              type: ACTION.FIRM_PORTFOLIO_REMOVE_COMMENT_FAIL,
              data: { id: params.portfolioId, type },
            });
            ResponseErrorDialog({ response });
          },
          handleSuccess: () => {
            dispatch({
              type: ACTION.FIRM_PORTFOLIO_REMOVE_COMMENT_SUCCESS,
              data: {
                id: params.portfolioId,
                type: type,
                commentId: params.commentId,
              },
            });
          },
        }
      );
    });
  };

  const handlePortfolioItemShowPreviousComments = params => {
    const type = params.type.toLowerCase();
    dispatch({
      type: ACTION.FIRM_PORTFOLIO_COMMENTS_REQUEST,
      data: { id: params.portfolioId, type },
    });

    api.get(
      ENDPOINT.ACTION.FIRM.LOAD_PORTFOLIO_COMMENTS,
      {
        id: state.firm.FirmID,
        portfolioId: params.portfolioId,
        emId: params.commentId || 0,
      },
      {
        handleError: response => {
          dispatch({
            type: ACTION.FIRM_PORTFOLIO_COMMENTS_FAIL,
            data: { id: params.portfolioId, type },
          });
          ResponseErrorDialog({ response });
        },
        handleSuccess: responseData => {
          dispatch({
            type: ACTION.FIRM_PORTFOLIO_COMMENTS_SUCCESS,
            data: {
              id: params.portfolioId,
              type: type,
              comments: responseData,
            },
          });
        },
      }
    );
  };

  const handleReviewAddComment = params => {
    dispatch({
      type: ACTION.FIRM_REVIEW_ADD_COMMENT_REQUEST,
      data: { id: params.reviewId, type: params.type },
    });

    api.post(
      ENDPOINT.ACTION.FIRM.ADD_REVIEW_COMMENT,
      {
        id: state.firm.FirmID,
        emId: params.reviewId,
        type: params.type,
        comment: params.comment,
      },
      {
        handleError: response => {
          dispatch({
            type: ACTION.FIRM_REVIEW_ADD_COMMENT_FAIL,
            data: { id: params.reviewId, type: params.type },
          });
          ResponseErrorDialog({ response });
        },
        handleSuccess: responseData => {
          dispatch({
            type: ACTION.FIRM_REVIEW_ADD_COMMENT_SUCCESS,
            data: {
              id: params.reviewId,
              type: params.type,
              comment: responseData,
            },
          });
        },
      }
    );
  };

  const handleReviewRemoveComment = params => {
    ConfirmDialog({
      bodyRender: `Are you sure you want to delete this comment?`,
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
      textCancelBtn: 'No',
    }).then(() => {
      dispatch({
        type: ACTION.FIRM_REVIEW_REMOVE_COMMENT_REQUEST,
        data: { id: params.reviewId, type: params.type },
      });

      api.post(
        ENDPOINT.ACTION.FIRM.DELETE_REVIEW_COMMENT,
        {
          id: params.commentId,
          type: params.type,
        },
        {
          handleError: response => {
            dispatch({
              type: ACTION.FIRM_REVIEW_REMOVE_COMMENT_FAIL,
              data: { id: params.reviewId, type: params.type },
            });
            ResponseErrorDialog({ response });
          },
          handleSuccess: () => {
            dispatch({
              type: ACTION.FIRM_REVIEW_REMOVE_COMMENT_SUCCESS,
              data: {
                id: params.reviewId,
                type: params.type,
                commentId: params.commentId,
              },
            });
          },
        }
      );
    });
  };

  const handleReviewShowPreviousComments = params => {
    dispatch({
      type: ACTION.FIRM_REVIEW_COMMENTS_REQUEST,
      data: { id: params.reviewId, type: params.type },
    });

    api.get(
      ENDPOINT.ACTION.FIRM.LOAD_REVIEW_COMMENTS,
      {
        id: state.firm.FirmID,
        reviewId: params.reviewId,
        type: params.type,
        emId: params.commentId || 0,
      },
      {
        handleError: response => {
          dispatch({
            type: ACTION.FIRM_REVIEW_COMMENTS_FAIL,
            data: { id: params.reviewId, type: params.type },
          });
          ResponseErrorDialog({ response });
        },
        handleSuccess: responseData => {
          dispatch({
            type: ACTION.FIRM_REVIEW_COMMENTS_SUCCESS,
            data: {
              id: params.reviewId,
              type: params.type,
              comments: responseData,
            },
          });
        },
      }
    );
  };

  return (
    <PageLayout
      pageClass="firm"
      pageTitle={firmname}
      headerContent={
        state.init ? (
          <CSSTransition
            in={true}
            appear={true}
            timeout={ANIMATION_TIMEOUT_APPEAR}
            classNames="fade"
          >
            <FirmHeader
              isLoggedIn={user.isLoggedIn}
              baseUrl={state.baseUrl}
              firm={state.firm}
              tabs={state.nav}
              activeTab={currentNavTab}
              isEditMode={state.isEditMode}
              showEditFirmInfoBtn={state.permissions.AllowedEditMod}
              onClickEditFirmInfo={toggleEditMode}
              onClickRatingUpdate={updateFirmRating}
              onClickTabActiveToggle={toggleTabActive}
              onClickAddReview={gotoFormReview}
              onClickResponses={gotoReviewsTab}
              onLogoEdit={editLogo}
              onLogoDelete={deleteLogo}
            />
          </CSSTransition>
        ) : (
          <FirmHeaderContentLoader />
        )
      }
      mainContent={
        state.init ? (
          <CSSTransition
            in={true}
            appear={true}
            timeout={ANIMATION_TIMEOUT_APPEAR}
            classNames="fade"
          >
            <TransitionGroup component={null}>
              <Transition
                key={pathname}
                timeout={ANIMATION_TIMEOUT_APPEAR}
                in={true}
                mountOnEnter={true}
                unmountOnExit={false}
                onEnter={node => handleEnterAnimation(node)}
                onExit={node => handleExitAnimation(node)}
              >
                <>
                  <Switch>
                    <Route
                      path={ROUTE_REGEXP.FIRM_SECTION_REVIEWS}
                      exact
                      render={() => (
                        <FirmReviews
                          user={user}
                          firm={state.firm}
                          reviews={state.tabs.reviews.data}
                          sortBy={state.reviewsSortBy}
                          hasPagination={true}
                          actions={{
                            gotoFormReview,
                            gotoReviewsTab,
                            handleReviewSorted,
                            handleClickReviewLink,
                            handleBeforeReviewRatingUpdate,
                            handleReviewRatingUpdate,
                            handleReviewShowPreviousComments,
                            handleReviewAddComment,
                            handleReviewRemoveComment,
                          }}
                        />
                      )}
                    />

                    {(state.tabs.about.isActive || state.isEditMode) && (
                      <Route
                        path={ROUTE_REGEXP.FIRM_SECTION_ABOUT}
                        exact
                        render={() => (
                          <FirmAbout
                            isPhotosSortable={state.isPhotosSortable}
                            firm={state.firm}
                            descriptionBlock={
                              state.tabs.about.data.descriptionBlock
                            }
                            descriptionSubBlocks={
                              state.tabs.about.data.descriptionSubBlocks
                            }
                            awardsBlock={state.tabs.about.data.awardsBlock}
                            photosBlock={state.tabs.about.data.photosBlock}
                            AllowedToEdit={state.permissions.AllowedEditMod}
                            editMode={state.isEditMode}
                            onPhotoGallerySorting={handlePhotoGallerySorting}
                            onPhotoGalleryChangeSortingMode={
                              togglePhotoGallerySortingMode
                            }
                            onPhotoDelete={handlePhotoDelete}
                            onPhotoUpload={handlePhotoUpload}
                            onDescriptionBlockSave={handleDescriptionBlockSave}
                            onDescriptionSubBlockAdd={
                              handleDescriptionSubBlockAdd
                            }
                            onDescriptionSubBlockUpdate={
                              handleDescriptionSubBlockUpdate
                            }
                            onDescriptionSubBlockDelete={
                              handleDescriptionSubBlockDelete
                            }
                            onAwardsBlockSave={handleAwardsBlockSave}
                            onAwardsBlockDelete={handleAwardsBlockDelete}
                            onDescriptionBlock={handleDescriptionBlock}
                          />
                        )}
                      />
                    )}

                    {state.tabs.portfolio &&
                      (state.tabs.portfolio.isActive || state.isEditMode) && (
                        <Route
                          path={ROUTE_REGEXP.FIRM_SECTION_PORTFOLIO}
                          exact
                          render={() => (
                            <FirmPortfolio
                              user={user}
                              firmId={state.firm.FirmID}
                              criteria={state.tabs.portfolio.data.criteria}
                              grids={state.tabs.portfolio.data}
                              isEditMode={state.isEditMode}
                              onClickDealButton={handleDealButton}
                              onClickPage={fetchPortfolioGrid}
                              onEditPortfolioItem={handleEditPortfolioItem}
                              onDeletePortfolioItem={handleDeletePortfolioItem}
                              onAddPortfolioItem={handleAddPortfolioItem}
                              onCriteriaUpdate={handleCriteriaUpdate}
                              onAddPortfolioComment={
                                handlePortfolioItemAddComment
                              }
                              onRemovePortfolioComment={
                                handlePortfolioItemRemoveComment
                              }
                              onClickShowPortfolioPreviousComments={
                                handlePortfolioItemShowPreviousComments
                              }
                            />
                          )}
                        />
                      )}

                    <Route
                      path={ROUTE_REGEXP.FIRM_REVIEW_FORM}
                      exact
                      render={() => (
                        <ReviewFormSwitcher
                          firm={{
                            label: state.firm.FirmName,
                            value: state.firm.FirmID,
                            rating: state.firm.FirmCandidateRating,
                            type:
                              state.firm.TypeID === '1'
                                ? 'recruiter'
                                : 'non-recruiter',
                          }}
                          user={user}
                          formType={formType}
                          onChange={handleChangeForm}
                        />
                      )}
                    />

                    <Route
                      path={ROUTE_REGEXP.FIRM_PROFILE}
                      exact
                      render={() => (
                        <>
                          <FirmOverview
                            firm={state.firm}
                            isEditMode={state.isEditMode}
                            onEditOverview={handleEditOverview}
                          />
                          <FirmReviews
                            user={user}
                            firm={state.firm}
                            reviews={state.tabs.reviews.data}
                            sortBy={state.reviewsSortBy}
                            hasPagination={false}
                            actions={{
                              gotoFormReview,
                              gotoReviewsTab,
                              handleReviewSorted,
                              handleClickReviewLink,
                              handleBeforeReviewRatingUpdate,
                              handleReviewRatingUpdate,
                              handleReviewShowPreviousComments,
                              handleReviewAddComment,
                              handleReviewRemoveComment,
                            }}
                          />
                        </>
                      )}
                    />

                    {state.tabs.strategy &&
                      (state.tabs.strategy.isActive || state.isEditMode) && (
                        <Route
                          path={ROUTE_REGEXP.FIRM_SECTION_STRATEGY}
                          exact
                          render={() => (
                            <FirmStrategy
                              isEditMode={state.isEditMode}
                              firm={state.firm}
                              funds={state.tabs.strategy.data.funds}
                              investment={state.tabs.strategy.data.investment}
                              onAddFundHistory={handleAddFundHistory}
                              onEditFundHistory={handleEditFundHistory}
                              onDeleteFundHistory={handleDeleteFundHistory}
                              onEditInvestmentStrategy={
                                handleEditInvestmentStrategy
                              }
                              onDeleteInvestmentStrategy={
                                handleDeleteInvestmentStrategy
                              }
                            />
                          )}
                        />
                      )}

                    <Redirect to={state.baseUrl} />
                  </Switch>

                  {state.tabs.reviews.data.rowsCount >
                    state.tabs.reviews.data.perPage &&
                    tab === 'reviews' && (
                      <PagePagination
                        page={state.tabs.reviews.data.page}
                        perPage={state.tabs.reviews.data.perPage}
                        count={state.tabs.reviews.data.rowsCount}
                        pageRangeDisplayed={state.tabs.reviews.data.perPage}
                        hideNavigation={true}
                        hideFirstLastPages={false}
                        controlFunc={handleClickReviewPage}
                      />
                    )}
                </>
              </Transition>
            </TransitionGroup>
          </CSSTransition>
        ) : (
          <>
            <FirmOverviewContentLoader />
            <FirmReviewsContentLoader />
            <div className="horizontal-loader__container">
              <HorizontalLoader />
            </div>
          </>
        )
      }
      sidebarContent={
        state.init ? (
          <CSSTransition
            in={true}
            appear={true}
            timeout={ANIMATION_TIMEOUT_APPEAR}
            classNames="fade"
          >
            <Sidebar>
              {state.relativeFirms.length > 0 && (
                <MediaQuery query="(max-width: 767px)">
                  <div className="sidebar__panel">
                    <FullWidthCarousel
                      title="Candidates Also Viewed"
                      items={state.relativeFirms}
                      onRatingUpdate={handleCandidatesAlsoViewedRatingUpdate}
                      onBeforeRatingUpdate={
                        handleCandidatesAlsoViewedBeforeRatingUpdate
                      }
                    />
                  </div>
                </MediaQuery>
              )}

              {Permission.isAllowedViewFirmEmployees(user.permissions) && (
                <FirmEmployees
                  candidateId={user.cId}
                  firmId={state.firm.FirmID}
                  actions={networkAction}
                />
              )}
              {!user.isLoggedIn && (
                <CtaBanner
                  blockHeight="150"
                  blockBg={ctaBackground}
                  blockText="Recruit Talent Through GoBuyside"
                  blockFontSize="20"
                />
              )}
              {<RelatedJobs user={user} history={history} />}
            </Sidebar>
          </CSSTransition>
        ) : (
          <FiltersContentLoader />
        )
      }
      fullWidthContent={
        state.relativeFirms.length > 0 && (
          <MediaQuery query="(min-width: 768px)">
            <div className="container">
              <FullWidthCarousel
                title="Candidates Also Viewed"
                items={state.relativeFirms}
                onRatingUpdate={handleCandidatesAlsoViewedRatingUpdate}
                onBeforeRatingUpdate={
                  handleCandidatesAlsoViewedBeforeRatingUpdate
                }
              />
            </div>
          </MediaQuery>
        )
      }
    />
  );
};

export default withRouter(withNetworkActions(Firm));
